import { format } from 'date-fns';
import { REPORT_EXPORT_STEPS, type ReportExportJob, type ReportExportStep, type ReportExportType } from '../../shared/schemas/report-export/report-export.schema';

// MF-1143: como o painel de exportações mostra uma tarefa. Puro (sem React) para ser testado com jest.

export const REPORT_EXPORT_LABELS: Record<ReportExportType, string> = {
	cash_flow: 'Fluxo de Caixa',
	nfe: 'Portal de NFe',
	budget: 'Orçamentos',
	plans: 'Planejamentos',
	finance_history: 'Livro Caixa',
	receivables: 'Contas a Receber',
	payables: 'Contas a Pagar',
	transfers: 'Produção',
	procedures: 'Procedimentos'
};

const FORMAT_LABELS: Record<ReportExportJob['format'], string> = {
	pdf: 'PDF',
	xlsx: 'Excel'
};

const STEP_LABELS = new Map<ReportExportStep, string>([
	['validating', 'Validando a solicitação'],
	['querying', 'Consultando os dados'],
	['generating', 'Gerando o arquivo'],
	['storing', 'Armazenando o arquivo']
]);

/** Etapa final, que não é do worker: o arquivo pronto para baixar. */
const AVAILABLE_STEP_LABEL = 'Disponível para download';

export type ReportExportStepState = 'done' | 'current' | 'pending' | 'failed';
export type ReportExportTone = 'info' | 'success' | 'warning' | 'danger' | 'muted';

export interface ReportExportStepView {
	key: ReportExportStep | 'available';
	label: string;
	state: ReportExportStepState;
}

export interface ReportExportView {
	title: string;
	statusLabel: string;
	tone: ReportExportTone;
	/** Etapas com estado; vazio quando não ajudam (fila, cancelada). */
	steps: ReportExportStepView[];
	/** Motivo da falha ou da retentativa, já escrito para o usuário pelo backend. */
	detail: string | null;
	/** "Disponível até 17/10 às 14:32", ou "Expirou em ..." */
	availability: string | null;
	/** Momento em que os dados foram lidos: o arquivo é uma fotografia desse momento. */
	dataReference: string | null;
	isActive: boolean;
	canCancel: boolean;
	canDownload: boolean;
	canRetry: boolean;
}

const ACTIVE = new Set<ReportExportJob['status']>(['queued', 'processing', 'retrying', 'cancelling']);

export const isReportExportActive = (job: Pick<ReportExportJob, 'status'>): boolean => ACTIVE.has(job.status);

/** Contadores do painel minimizado: em andamento, prontas e com erro (canceladas e expiradas não contam). */
export interface ReportExportCounts {
	active: number;
	ready: number;
	failed: number;
}

export const countReportExports = (jobs: Array<Pick<ReportExportJob, 'status'>>): ReportExportCounts => ({
	active: jobs.filter(isReportExportActive).length,
	ready: jobs.filter(job => job.status === 'completed').length,
	failed: jobs.filter(job => job.status === 'failed').length
});

export type ReportExportBadge = 'failed' | 'ready' | 'active';

/** Ícone do painel oculto: erro vence pronto, que vence em andamento. Sem nenhum dos três, o ícone some. */
export const reportExportBadge = ({ active, ready, failed }: ReportExportCounts): ReportExportBadge | null => {
	if (failed > 0) return 'failed';
	if (ready > 0) return 'ready';
	if (active > 0) return 'active';
	return null;
};

const formatDateTime = (iso: string): string => format(new Date(iso), "dd/MM 'às' HH:mm");

function buildSteps(job: ReportExportJob): ReportExportStepView[] {
	const all: Array<ReportExportStep | 'available'> = [...REPORT_EXPORT_STEPS, 'available'];
	const labelOf = (key: ReportExportStep | 'available') => (key === 'available' ? AVAILABLE_STEP_LABEL : (STEP_LABELS.get(key) ?? key));

	if (job.status === 'completed') {
		return all.map(key => ({ key, label: labelOf(key), state: 'done' }));
	}
	// Expirada: o arquivo não está mais disponível, então "Disponível para download" não vale mais.
	if (job.status === 'expired') return [];

	const currentIndex = job.step ? all.indexOf(job.step) : -1;
	if (currentIndex < 0) return [];

	const currentState: ReportExportStepState = job.status === 'failed' ? 'failed' : 'current';
	return all.map((key, index) => ({
		key,
		label: labelOf(key),
		state: index < currentIndex ? 'done' : index === currentIndex ? currentState : 'pending'
	}));
}

export function describeReportExport(job: ReportExportJob): ReportExportView {
	const title = `${REPORT_EXPORT_LABELS[job.report_type] ?? 'Relatório'} (${FORMAT_LABELS[job.format] ?? job.format})`;
	const base = {
		title,
		steps: buildSteps(job),
		detail: null as string | null,
		availability: null as string | null,
		// Só existe arquivo (e portanto fotografia dos dados) quando a geração terminou.
		dataReference: job.data_reference_at && (job.status === 'completed' || job.status === 'expired') ? `Dados consultados em ${formatDateTime(job.data_reference_at)}` : null,
		isActive: isReportExportActive(job),
		canCancel: false,
		canDownload: false,
		canRetry: false
	};

	switch (job.status) {
		case 'queued':
			return { ...base, statusLabel: 'Na fila, aguardando processamento', tone: 'info', canCancel: true };
		case 'processing':
			return { ...base, statusLabel: `Processando${job.attempts > 1 ? ` (tentativa ${job.attempts} de ${job.max_attempts})` : ''}`, tone: 'info', canCancel: true };
		case 'retrying':
			return {
				...base,
				statusLabel: job.next_attempt_at
					? `Nova tentativa às ${format(new Date(job.next_attempt_at), 'HH:mm')} (${job.attempts + 1} de ${job.max_attempts})`
					: `Aguardando nova tentativa (${job.attempts + 1} de ${job.max_attempts})`,
				tone: 'warning',
				detail: job.error_message,
				canCancel: true
			};
		case 'cancelling':
			return { ...base, statusLabel: 'Cancelando...', tone: 'muted' };
		case 'cancelled':
			return { ...base, steps: [], statusLabel: 'Cancelada', tone: 'muted', canRetry: !job.filters_redacted };
		case 'completed':
			return {
				...base,
				statusLabel: 'Pronto para baixar',
				tone: 'success',
				availability: job.file_expires_at ? `Disponível até ${formatDateTime(job.file_expires_at)}` : null,
				canDownload: true
			};
		case 'expired':
			return {
				...base,
				statusLabel: 'Arquivo expirado',
				tone: 'muted',
				availability: job.file_expires_at ? `Expirou em ${formatDateTime(job.file_expires_at)}. Gere novamente para baixar.` : 'Gere novamente para baixar.',
				canRetry: !job.filters_redacted
			};
		case 'failed':
		default:
			return { ...base, statusLabel: 'Não foi possível gerar', tone: 'danger', detail: job.error_message, canRetry: !job.filters_redacted };
	}
}
